import { useState, useEffect } from 'react';
import { usePage } from '@inertiajs/react';
import { IOSInstructions } from './IOSInstructions';
import { AndroidInstructions } from './AndroidInstructions';
import PromptBanner from '../PromptBanner';
import type { SuggestedCartInfo } from '../../types/landing';

const NOTIF_DISMISS_KEY = 'notif-prompt-dismissed-until';
const PWA_DISMISS_KEY = 'pwa-dismissed-until';
const SUGGESTED_CART_DISMISS_KEY = 'suggested-cart-dismissed';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type PromptType = 'suggested-cart' | 'notifications' | 'install' | null;

function isDismissed(key: string): boolean {
    const until = localStorage.getItem(key);
    return !!until && Date.now() <= parseInt(until);
}

function dismiss(key: string) {
    localStorage.setItem(key, (Date.now() + THIRTY_DAYS_MS).toString());
}

const CartIcon = (
    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
        <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </div>
);

const BellIcon = (
    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
        <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </div>
);

const AppIcon = (
    <img
        src="/appicons/apple-touch-icon.png"
        alt="Abasto Virtual"
        className="w-12 h-12 rounded-xl"
    />
);

interface Props {
    hasOrders: boolean;
    suggestedCart: SuggestedCartInfo | null;
}

export default function LandingPrompts({ hasOrders, suggestedCart }: Props) {
    const { cartConflict } = usePage<{ cartConflict?: unknown }>().props;
    const [activePrompt, setActivePrompt] = useState<PromptType>(null);
    const [showInstructions, setShowInstructions] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [cartConflictPending, setCartConflictPending] = useState(!!cartConflict);

    // Wait for cart conflict banner to be resolved before showing any prompt
    useEffect(() => {
        if (!cartConflictPending) return;
        const handler = () => setCartConflictPending(false);
        window.addEventListener('cart-conflict-resolved', handler);
        return () => window.removeEventListener('cart-conflict-resolved', handler);
    }, [cartConflictPending]);

    useEffect(() => {
        if (cartConflictPending) return;

        // Suggested cart: show for any authenticated user (doesn't require hasOrders)
        if (suggestedCart) {
            try {
                const stored = JSON.parse(localStorage.getItem(SUGGESTED_CART_DISMISS_KEY) || '{}');
                const stillDismissed = stored.code === suggestedCart.code && Date.now() <= stored.until;
                if (!stillDismissed) {
                    setActivePrompt('suggested-cart');
                    return;
                }
            } catch {
                setActivePrompt('suggested-cart');
                return;
            }
        }

        // Notifications and install require hasOrders
        if (!hasOrders) return;

        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || (('standalone' in navigator) && (navigator as any).standalone);

        const timer = setTimeout(() => {
            // 1. Notifications prompt
            if (!isDismissed(NOTIF_DISMISS_KEY) && typeof window !== 'undefined' && (window as any).OneSignal) {
                (window as any).OneSignalDeferred = (window as any).OneSignalDeferred || [];
                (window as any).OneSignalDeferred.push(async (os: any) => {
                    try {
                        const permission = os.Notifications.permission;
                        const optedIn = await os.User.PushSubscription.optedIn;
                        if (permission !== true && !optedIn) {
                            setActivePrompt('notifications');
                            return;
                        }
                    } catch {
                        // Fall through to install check
                    }

                    if (isMobile && !isStandalone && !isDismissed(PWA_DISMISS_KEY)) {
                        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
                        setIsIOS(isIOSDevice);
                        setActivePrompt('install');
                    }
                });
                return;
            }

            // 2. Install prompt (mobile only)
            if (isMobile && !isStandalone && !isDismissed(PWA_DISMISS_KEY)) {
                const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
                setIsIOS(isIOSDevice);
                setActivePrompt('install');
            }
        }, 2000);

        // Listen for beforeinstallprompt (Android/Chrome)
        const installHandler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };
        window.addEventListener('beforeinstallprompt', installHandler);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('beforeinstallprompt', installHandler);
        };
    }, [hasOrders, suggestedCart, cartConflictPending]);

    const dismissSuggestedCart = () => {
        if (!suggestedCart) return;
        try {
            localStorage.setItem(SUGGESTED_CART_DISMISS_KEY, JSON.stringify({
                code: suggestedCart.code,
                until: Date.now() + FOURTEEN_DAYS_MS,
            }));
        } catch {}
    };

    const handleDismiss = () => {
        if (activePrompt === 'suggested-cart') {
            dismissSuggestedCart();
        } else if (activePrompt === 'notifications') {
            dismiss(NOTIF_DISMISS_KEY);
        } else if (activePrompt === 'install') {
            dismiss(PWA_DISMISS_KEY);
        }
        setActivePrompt(null);
        setShowInstructions(false);
    };

    const handleActivateNotifications = async () => {
        try {
            const OneSignalDeferred = (window as any).OneSignalDeferred || [];
            OneSignalDeferred.push(async (os: any) => {
                await os.Notifications.requestPermission();
                try { await os.User.PushSubscription.optIn(); } catch {}
            });
        } catch {}
        dismiss(NOTIF_DISMISS_KEY);
        setActivePrompt(null);
    };

    const handleInstall = async () => {
        if (deferredPrompt) {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setActivePrompt(null);
                setShowInstructions(false);
            }
            setDeferredPrompt(null);
        }
    };

    if (!activePrompt) return null;

    // Install instructions modal
    if (showInstructions && activePrompt === 'install') {
        return (
            <div className="fixed inset-0 z-[9999] bg-black/60 flex items-end sm:items-center justify-center p-4">
                <div className="bg-white rounded-xl p-5 shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={() => setShowInstructions(false)}
                            className="text-gray-400 hover:text-gray-700 transition-colors flex items-center gap-1 text-sm bg-transparent border-none cursor-pointer"
                        >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Volver
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="text-gray-400 hover:text-gray-700 p-1 bg-transparent border-none cursor-pointer"
                            aria-label="Cerrar"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>

                    {isIOS ? (
                        <IOSInstructions />
                    ) : (
                        <AndroidInstructions
                            onInstall={deferredPrompt ? handleInstall : undefined}
                            hasNativePrompt={!!deferredPrompt}
                        />
                    )}

                    <button
                        onClick={handleDismiss}
                        className="mt-4 w-full py-2 text-sm text-gray-500 hover:text-gray-800 transition-colors border border-gray-300 rounded-lg bg-transparent cursor-pointer"
                    >
                        Quizás más tarde
                    </button>
                </div>
            </div>
        );
    }

    // Suggested cart banner
    if (activePrompt === 'suggested-cart' && suggestedCart) {
        const subtitle =
            suggestedCart.source === 'orders'
                ? `Basándonos en tus últimas compras, armamos un carrito con ${suggestedCart.items_count} productos.`
                : `Preparamos una selección de ${suggestedCart.items_count} productos de temporada para vos.`;

        const handleViewSuggestedCart = () => {
            dismissSuggestedCart();
            window.location.href = `/carrito?sc=${suggestedCart.code}`;
        };

        return (
            <PromptBanner
                icon={CartIcon}
                title="Tenemos un carrito para vos"
                description={subtitle}
                onClose={handleDismiss}
                dismissLabel="Ahora no"
                actionLabel="Ver carrito"
                onAction={handleViewSuggestedCart}
            />
        );
    }

    // Notification banner
    if (activePrompt === 'notifications') {
        return (
            <PromptBanner
                icon={BellIcon}
                title="Activar notificaciones"
                description="Recibí alertas sobre tus pedidos y novedades."
                onClose={handleDismiss}
                dismissLabel="Ahora no"
                actionLabel="Activar"
                onAction={handleActivateNotifications}
            />
        );
    }

    // Install app banner (mobile only)
    return (
        <PromptBanner
            icon={AppIcon}
            title="Instalar Abasto Virtual"
            description="Agregá la app a tu pantalla de inicio para acceso rápido."
            onClose={handleDismiss}
            dismissLabel="Ahora no"
            actionLabel={deferredPrompt ? 'Instalar' : 'Cómo instalar'}
            onAction={deferredPrompt ? handleInstall : () => setShowInstructions(true)}
        />
    );
}
